/* eslint-disable */
import React, { useEffect, useState, useRef } from "react";
import * as XLSX from "xlsx";
import * as cptable from "codepage";
XLSX.set_cptable(cptable);

import * as url from "helper/UrlHelper";
import { postData, uploadSingleFile } from "utils/Gateway";

const CsvCustomUploadModal = ({ afterModalClose = () => {} }) => {
  const fileInputRef = useRef(null);

  const token = localStorage.getItem("token");

  // Update field names and labels for companies
  const companyFields = {
    name: { required: true, label: "Company Name" },
    source: {required: false, label: "Source"},
    owner: { required: false, label: "Contact Name" },
    email: { required: false, label: "Contact Email" },
    classification: {required: false, label: "Classification"},
    phone: { required: false, label: "Phone Number" },
    cif: { required: false, label: "CIF" },
    cnae: {required: false, label: "CNAE"},
    location: { required: false, label: "Address" },
    city: { required: false, label: "City" },
    state: { required: false, label: "State/Province" },
    country: { required: false, label: "Country" },
    zipCode: { required: false, label: "ZIP/Postal Code" },
    noofemployees: { required: false, label: "No. of Employees" },
    billing: { required: false, label: "Sales" },
    billingyear: {required: false, label: "Billing Year"},
    website: { required: false, label: "Website" },
    linkedin: { required: false, label: "LinkedIn Url" },
    summary: { required: false, label: "Brief about company" },
    manager: { required: false, label: "Manager Name" },
    manageremail: { required: false, label: "Manager Email" },
    list: { required: false, label: "List Name" },
    group: { required: false, label: "Group Name" },
    groupowner: { required: false, label: "Group Owner Name" },
    groupowneremail: { required: false, label: "Group Owner Email" },
  };

  const [fileData, setFileData] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [validationError, setValidationError] = useState("");
  const [invalidFields, setInvalidFields] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadStats, setUploadStats] = useState({
    leadsCreated: 0,
    leadsUpdated: 0,
    totalProcessed: 0,
    companyRecords: [],
  });

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploadStats({
      leadsCreated: 0,
      leadsUpdated: 0,
      totalProcessed: 0,
      companyRecords: [],
    });
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
          header: 1,
        });
        if (sheet.length > 1) {
          const cleanHeaders = sheet[0]
            .map((header) => (header ? header.toString().trim() : ""))
            .filter(Boolean);
          setHeaders(cleanHeaders);
          setFileData(sheet.slice(1).filter((row) => row.some((cell) => cell)));
          setValidationError("");
        } else {
          setValidationError("The file appears to be empty or invalid.");
        }
      } catch (error) {
        setValidationError(
          "Error reading file. Please ensure it's a valid Excel or CSV file."
        );
        console.error("File reading error:", error);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleMappingChange = (field, headerValue) => {
    setMapping((prev) => ({ ...prev, [field]: headerValue || undefined }));
  };

  const validateMapping = () => {
    const requiredFields = Object.keys(companyFields).filter(
      (field) => companyFields[field].required
    );
    const unmappedRequired = requiredFields.filter((field) => !mapping[field]);
    if (unmappedRequired.length > 0) {
      setInvalidFields(unmappedRequired);
      setValidationError(
        `Please map these required fields: ${unmappedRequired
          .map((f) => companyFields[f].label)
          .join(", ")}`
      );
      return false;
    }
    setInvalidFields([]);
    setValidationError("");
    return true;
  };

  const handleSubmit = async () => {
    if (!validateMapping()) return;
    setIsLoading(true);

    // send the data to API
    if (fileData && Object.keys(mapping).length > 0) {
      const batchSize = 50;
      const totalBatches = Math.ceil(fileData.length / batchSize);

      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const batchData = fileData.slice(
          batchIndex * batchSize,
          (batchIndex + 1) * batchSize
        );

        const payload = {
          mapping,
          header: headers,
          filedata: batchData,
        };

        console.log("Payload for batch", batchIndex + 1, ":", payload);

        try {
          // Update API endpoint for companies
          let requestUrl = `${url.API_BASE_URL}${url.API_SAVE_COMPANIES_MASSIVEUPLOAD}?token=${token}`;

          console.log("Request URL:", requestUrl);

          const response = await postData(requestUrl, payload);

          console.log("Response for batch", batchIndex + 1, ":", response);

          if (response.status) {
            // Update upload stats
            setUploadStats((prev) => ({
              leadsCreated:
                prev.leadsCreated + (response.data.recordsInserted || 0),
              leadsUpdated:
                prev.leadsUpdated + (response.data.recordsUpdated || 0),
              totalProcessed: prev.totalProcessed + batchData.length,
              companyRecords: response.data.companyRecords,
            }));
          }
        } catch (error) {
          console.error("Error processing batch", batchIndex + 1, ":", error);
        }
      }
    }

    setIsLoading(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFileData(null);
    setHeaders([]);
    setMapping({});
    setValidationError("");
    setInvalidFields([]);
    setUploadStats({
      leadsCreated: 0,
      leadsUpdated: 0,
      totalProcessed: 0,
      companyRecords: [],
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const getSampleValue = (field) => {
    if (!mapping[field] || !fileData || fileData.length === 0) return "";
    const headerIndex = headers.indexOf(mapping[field]);
    if (headerIndex === -1) return "";
    for (let row of fileData) {
      if (row[headerIndex] && row[headerIndex].toString().trim())
        return row[headerIndex].toString().trim();
    }
    return "";
  };

  return (
    <div
      className="modal fade"
      id="csvUploadModal"
      tabIndex="-1"
      aria-labelledby="csvUploadModalLabel"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div className="modal-dialog modal-xl" style={{ maxWidth: "90vw" }}>
        <div className="modal-content bg-white border-0 rounded-15 shadow-lg">
          {/* Enhanced Header with Icon and Gradient Background */}
          <div className="modal-header bg-primary text-white px-4 py-3 border-bottom border-gray-300">
            <div className="d-flex align-items-center gap-3">
              <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
                <span className="material-symbols-outlined text-primary fs-5 icon-md">
                  upload_file
                </span>
              </div>
              <div>
                <h3
                  className="fw-bold fs-2 text-white mb-0"
                  id="csvUploadModalLabel"
                >
                  Upload Companies from Excel/CSV
                </h3>
                <p className="text-white fs-sm mb-0">
                  Upload multiple companies at once using this feature.
                </p>
              </div>
            </div>
            <button
              type="button"
              className="btn-close p-2 bg-gray-200 rounded-circle shadow-sm border-0"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={handleCloseModal}
            ></button>
          </div>

          <div className="modal-body p-4">
            <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm">
              {isLoading && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-white bg-opacity-75"
                  style={{ zIndex: 1050 }}
                >
                  <div
                    className="spinner-border text-primary mb-3"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-primary fw-semibold mb-0">
                    Uploading companies, please wait...
                  </p>
                </div>
              )}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label
                    htmlFor="fileInput"
                    className="form-label fs-md fw-semibold"
                  >
                    Choose File
                  </label>
                  <input
                    type="file"
                    className="form-control bg-white shadow-none border-gray-300"
                    id="fileInput"
                    ref={fileInputRef}
                    accept=".csv,.xls,.xlsx"
                    onChange={handleFileUpload}
                  />
                </div>
                <div className="col-md-6">
                  {fileData && (
                    <>
                      <label className="form-label fs-md fw-semibold">
                        File Loaded Successfully
                      </label>
                      <div className="text text-primary d-flex align-items-center fw-semibold">
                        <i className="bi bi-check-circle me-2"></i>
                        Rows Found to be uploaded: {fileData.length}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {headers.length > 0 && (
                <div className="mt-4 p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm">
                  <h5 className="border-bottom pb-2 mb-4 fs-md fw-semibold">
                    Map Excel Columns to Company Fields
                  </h5>
                  <div className="row mb-2">
                    <div className="col-md-4">
                      <strong className="fs-sm">Field Name</strong>
                    </div>
                    <div className="col-md-4">
                      <strong className="fs-sm">Excel Column</strong>
                    </div>
                    <div className="col-md-4">
                      <strong className="fs-sm">Sample Value</strong>
                    </div>
                  </div>
                  {Object.entries(companyFields).map(([field, config]) => (
                    <div className="row mb-2 align-items-center" key={field}>
                      <div className="col-md-4">
                        <label className="fs-sm">
                          {config.label}{" "}
                          {config.required && (
                            <span className="text-danger">*</span>
                          )}
                        </label>
                      </div>
                      <div className="col-md-4">
                        <select
                          className={`form-select bg-white shadow-none border-gray-300 fs-sm ${
                            invalidFields.includes(field) ? "is-invalid" : ""
                          }`}
                          value={mapping[field] || ""}
                          onChange={(e) =>
                            handleMappingChange(field, e.target.value)
                          }
                        >
                          <option value="">Select column...</option>
                          {headers.map((header, idx) => (
                            <option key={idx} value={header}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <small className="fs-sm">
                          {getSampleValue(field) || "—"}
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {validationError && (
                <div className="alert alert-danger mt-4 d-flex align-items-center fw-semibold">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {validationError}
                </div>
              )}

              {(fileData && fileData.length > 0) || isLoading ? (
                <div
                  className="card shadow-sm border-primary mt-4"
                  style={{ maxWidth: 500 }}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-bar-chart-fill text-primary fs-4 me-2"></i>
                      <h6 className="mb-0 fw-bold text-primary">
                        Upload Report
                      </h6>
                    </div>
                    <div className="row text-center">
                      <div className="col-3">
                        <div className="text-secondary fw-bold fs-5">
                          <i className="bi bi-collection me-1"></i>
                          {fileData && fileData.length ? fileData.length : 0}
                        </div>
                        <div className="small text-muted">Total Records</div>
                      </div>
                      <div className="col-3">
                        <div className="text-success fw-bold fs-5">
                          <i className="bi bi-plus-circle me-1"></i>
                          {uploadStats.leadsCreated}
                        </div>
                        <div className="small text-muted">Created</div>
                      </div>
                      <div className="col-3">
                        <div className="text-warning fw-bold fs-5">
                          <i className="bi bi-arrow-repeat me-1"></i>
                          {uploadStats.leadsUpdated}
                        </div>
                        <div className="small text-muted">Updated</div>
                      </div>
                      <div className="col-3">
                        <div className="text-info fw-bold fs-5">
                          <i className="bi bi-list-ol me-1"></i>
                          {uploadStats.totalProcessed}
                        </div>
                        <div className="small text-muted">Total Processed</div>
                      </div>
                    </div>
                    {isLoading && (
                      <div className="mt-3 text-center">
                        <span className="spinner-border spinner-border-sm text-primary me-2"></span>
                        <span className="text-primary">Uploading...</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="modal-footer d-flex justify-content-end pt-3 border-top mt-4">
            {uploadStats.totalProcessed > 0 && (
              <button
                type="button"
                className="btn btn-success d-flex align-items-center gap-2"
                data-bs-dismiss="modal"
                onClick={() => {
                  console.log(
                    "uploadStats.companyRecords",
                    uploadStats.companyRecords
                  );
                  afterModalClose(uploadStats.companyRecords);
                  handleCloseModal();
                }}
              >
                <span className="material-symbols-outlined me-1">
                  check_circle
                </span>{" "}
                Close & Reload Companies
              </button>
            )}
            {Object.keys(mapping).length > 0 && (
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={handleSubmit}
                disabled={isLoading || !fileData}
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined me-1">
                      upload
                    </span>{" "}
                    Upload Companies
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CsvCustomUploadModal;
